// #359: a logged session as the slots of one model day, one round, for
// modelFromSource. One row per exercise: its heaviest set, once per work set.
// Keep free of React and any UI import.
import { blankSlot, type IntensityKind, type SetRowDraft, type SlotDraft } from "./programEdit.js";
import type { UnitsPreference } from "./schemas/profile.js";
import type { Workout, WorkoutExerciseEntry, WorkoutSet } from "./schemas/workout.js";
import { fromCanonicalKg } from "./units.js";

/** A set below this share of the heaviest set's load is a warm-up. By load,
 *  not by effort: not isApproachSet/EFFORT_APPROACH_RIR (effortScale.ts). */
export const WARM_UP_BELOW = 0.8;

export type WorkoutSlots = {
  slots: SlotDraft[];
  /** Sets left out as warm-up, over the whole session. */
  warmUps: number;
  /** Exercises whose load is an extra on the body, assisted ones included:
   *  ModelSource.bodyweight, so they go to history and the summary says so. */
  bodyweight: string[];
};

type LoadSet = Extract<WorkoutSet, { type: "weighted" | "bodyweight" }>;

const isLoadSet = (s: WorkoutSet): s is LoadSet => s.type === "weighted" || s.type === "bodyweight";

/** The load on the bar, or the extra on a bodyweight set. A drop set is its
 *  top round: the set itself, not its steps. */
const loadOf = (s: LoadSet): number => (s.type === "weighted" ? s.weight : (s.extra ?? 0));

const repsOf = (s: WorkoutSet): string => ("reps" in s && s.reps !== undefined ? String(s.reps) : "");

/** One set as a one-round row. */
const setRow = (kind: IntensityKind, reps: string, value: string, workKind?: "countup"): SetRowDraft => ({
  intensityKind: kind,
  ...(workKind ? { workKind } : {}),
  cells: [{ reps, intensityValue: value }],
  notesPerCell: [""],
});

/** The heaviest set; on a tie the one with most reps, then the first. */
const topSet = (sets: readonly LoadSet[]): LoadSet =>
  sets.reduce((a, b) => (loadOf(b) > loadOf(a) || (loadOf(b) === loadOf(a) && b.reps > a.reps) ? b : a));

type EntryRows = { rows: SetRowDraft[]; warmUps: number; bodyweight: boolean };

const entryRows = (entry: WorkoutExerciseEntry, unit: UnitsPreference): EntryRows => {
  const sets = entry.sets.filter(isLoadSet);
  // Timed, cardio, carries: a row the recognizer does not read, so no load.
  if (sets.length === 0)
    return { rows: entry.sets.map((s) => setRow("none", repsOf(s), "", "countup")), warmUps: 0, bodyweight: false };
  const top = topSet(sets);
  const load = loadOf(top);
  // With no added load every set is a work set.
  const work = load > 0 ? sets.filter((s) => loadOf(s) >= load * WARM_UP_BELOW).length : sets.length;
  const reps = String(top.reps);
  // A load wins over the effort: a row has one axis (plannedToRowCell).
  const make =
    load !== 0
      ? () => setRow("absolute", reps, String(fromCanonicalKg(load, unit)))
      : top.rpe !== undefined
        ? () => setRow("rpe", reps, String(top.rpe))
        : top.rir !== undefined
          ? () => setRow("rir", reps, String(top.rir))
          : () => setRow("none", reps, "");
  // An extra on the body, assisted (negative) included, is never a share of
  // the MAX: flagged here, not left to the catalog.
  const bodyweight = load !== 0 && (top.type === "bodyweight" || load < 0);
  return { rows: Array.from({ length: work }, make), warmUps: sets.length - work, bodyweight };
};

/** The session's entries as slots, loads in `unit`. */
export const workoutToSlotDrafts = (workout: Pick<Workout, "entries">, unit: UnitsPreference): WorkoutSlots => {
  let warmUps = 0;
  const bodyweight: string[] = [];
  const slots = workout.entries.map((entry) => {
    const out = entryRows(entry, unit);
    warmUps += out.warmUps;
    if (out.bodyweight) bodyweight.push(entry.exerciseId);
    return { ...blankSlot(1), exerciseId: entry.exerciseId, exerciseName: entry.exerciseName, setRows: out.rows };
  });
  return { slots, warmUps, bodyweight };
};
